import { ref, readonly, watch } from 'vue';
import { normalizeUploadedGeoJson } from '@/uploadedGeometryValidation';

/* ──────────────────────── Types ──────────────────────────── */

export interface AoiLibraryEntry {
  /** Display name derived from filename (without extension) */
  name: string;
  /** Original filename */
  filename: string;
  /** The GeoJSON content */
  geojson: Record<string, unknown>;
  /** ISO timestamp of when this AOI was added */
  addedAt: string;
}

/* ──────────────────────── State ──────────────────────────── */

const STORAGE_KEY = 'overpass.aoiLibrary.v1';
const entries = ref<AoiLibraryEntry[]>([]);

/* ──────────────────────── Persistence ─────────────────────── */

function hydrateFromStorage(): void {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;

    entries.value = parsed.flatMap((item: unknown) => {
      if (
        !item ||
        typeof item !== 'object' ||
        typeof (item as AoiLibraryEntry).name !== 'string' ||
        typeof (item as AoiLibraryEntry).filename !== 'string' ||
        (item as AoiLibraryEntry).geojson == null
      ) {
        return [];
      }

      const entry = item as AoiLibraryEntry;
      const normalized = normalizeUploadedGeoJson(entry.geojson);
      const normalizedGeoJson = normalized.geojson;
      if (normalized.error || !normalizedGeoJson) {
        console.warn('[aoi-library] Dropping invalid stored AOI entry:', entry.filename, normalized.error);
        return [];
      }

      return [{ ...entry, geojson: normalizedGeoJson }];
    });
  } catch {
    entries.value = [];
  }
}

if (typeof window !== 'undefined') {
  hydrateFromStorage();
}

watch(entries, (value) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}, { deep: true });

/* ──────────────────────── Actions ──────────────────────────── */

/**
 * Add a GeoJSON file to the persistent library.
 * Returns the entry (new or existing if same filename already present).
 */
function addEntry(filename: string, geojson: Record<string, unknown>): AoiLibraryEntry {
  const normalized = normalizeUploadedGeoJson(geojson);
  const normalizedGeoJson = normalized.geojson;
  if (normalized.error || !normalizedGeoJson) {
    throw new Error(normalized.error ?? 'Invalid GeoJSON');
  }

  // Derive display name from filename
  const name = filename.replace(/\.(geo)?json$/i, '') || 'Uploaded AOI';

  // Check for duplicate by filename
  const existing = entries.value.find((e) => e.filename === filename);
  if (existing) {
    // Update the geojson content
    entries.value = entries.value.map((e) =>
      e.filename === filename
        ? { ...e, geojson: normalizedGeoJson, addedAt: new Date().toISOString() }
        : e,
    );
    return entries.value.find((e) => e.filename === filename)!;
  }

  const entry: AoiLibraryEntry = {
    name,
    filename,
    geojson: normalizedGeoJson,
    addedAt: new Date().toISOString(),
  };

  entries.value = [entry, ...entries.value];
  return entry;
}

function removeEntry(filename: string): void {
  entries.value = entries.value.filter((e) => e.filename !== filename);
}

/* ──────────────────────── Composable ─────────────────────── */

export function useAoiLibrary() {
  return {
    entries: readonly(entries),
    addEntry,
    removeEntry,
  };
}
